import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  Button,
  FilterChips,
  IdChip,
  InfoHint,
  Input,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SearchInput,
  Select,
  StatusPill,
  Table,
  TableFooter,
  THead,
  TCell,
  TRow,
} from '@cou/admin-ui';
import { AdminShell } from '../shell/AdminShell';
import { useAdminAuth } from '../session/admin-auth-context';
import {
  defaultContestsClient,
  type AdminContestWithEntryCount,
  type ContestsClient,
} from '../contests/contests-client';
import { contestPillState } from '../contests/state-pill';
import { contestDetails } from '../contests/contest-details';
import { dateTimeLocalToIso, formatDateParts } from '../contests/format-date';
import { formatCount } from '../contests/format-number';
import { formatDuration, timeInStatusMs } from '../contests/duration';
import { defaultConfigClient, type ConfigClient } from '../config/config-client';

export interface ContestsListScreenProps {
  /** Injected for tests; defaults to the real API-09 client. */
  readonly client?: ContestsClient;
  /** Injected for tests; defaults to the real API-10 client (default_campaign_id pre-fill). */
  readonly configClient?: ConfigClient;
}

// Dot colors mirror StatusPill's own tone tokens so the filter chips and the row pills
// read as the same palette (contests-list design: Draft is a distinct chip from Scheduled).
const STATE_CHIPS = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft', dot: 'var(--color-state-draft-dot)' },
  { key: 'scheduled', label: 'Scheduled', dot: 'var(--color-state-scheduled-dot)' },
  { key: 'open', label: 'Open', dot: 'var(--color-state-open-dot)' },
  { key: 'closed', label: 'Closed', dot: 'var(--color-state-closed-dot)' },
  { key: 'settled', label: 'Settled', dot: 'var(--color-state-settled-dot)' },
  { key: 'canceled', label: 'Canceled', dot: 'var(--color-state-canceled-dot)' },
];

function pickCountLabel(config: Record<string, unknown>): string | null {
  const min = config['minPicks'];
  const max = config['maxPicks'];
  if (typeof max !== 'number') return null;
  if (typeof min === 'number' && min !== max) return `${min}–${max} picks`;
  return `${max} picks`;
}

export function ContestsListScreen({
  client = defaultContestsClient,
  configClient = defaultConfigClient,
}: ContestsListScreenProps) {
  const navigate = useNavigate();
  const { claims, getToken } = useAdminAuth();
  const canManage = claims?.permissions.includes('contests:manage') ?? false;
  const [contests, setContests] = useState<readonly AdminContestWithEntryCount[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('10');

  // Debounce the search box so typing doesn't fire a round-trip per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 whenever the filtered set could shrink out from under the current page
  // (a new filter/search/page-size) — otherwise the fetch could request an empty out-of-range
  // page.
  useEffect(() => {
    setPage(1);
  }, [stateFilter, debouncedSearch, pageSize]);

  const rowsPerPage = Number(pageSize);

  // Server-side filter, search, and pagination (CAE-298) — the list can grow well past what's
  // worth shipping to the client in one response, so state/search/limit/offset all travel as
  // query params and the backend does the work.
  useEffect(() => {
    const token = getToken();
    if (token === null) return;
    let cancelled = false;
    void (async () => {
      const result = await client.listContests(token, {
        state: stateFilter === 'all' ? undefined : stateFilter,
        search: debouncedSearch.trim() || undefined,
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      });
      if (cancelled) return;
      if (result.ok) {
        setContests(result.contests);
        setTotal(result.total);
        setCounts(result.counts);
        setLoadError(null);
      } else {
        setLoadError(`Could not load contests (${result.code ?? result.status}).`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, getToken, stateFilter, debouncedSearch, page, rowsPerPage]);

  const chips = useMemo(
    () =>
      STATE_CHIPS.map((chip) => ({
        ...chip,
        count:
          chip.key === 'all'
            ? Object.values(counts).reduce((sum, n) => sum + n, 0)
            : (counts[chip.key] ?? 0),
      })),
    [counts],
  );

  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const currentPage = Math.min(page, pageCount);
  const pageStart = (currentPage - 1) * rowsPerPage;

  // A total that shrinks from under us (a Contest deleted elsewhere) can strand `page` past the
  // last page, leaving the fetch asking for an empty range. The fetch can't clamp itself —
  // pageCount only arrives with the response it would be clamping — so correct it afterwards.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  return (
    <AdminShell
      title="Contests"
      subtitle="Manage all contests and their schedules."
      actions={canManage && <Button onClick={() => setCreateModalOpen(true)}>+ New contest</Button>}
    >
      {loadError && <p className="font-ui text-[13px] text-danger">{loadError}</p>}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <FilterChips chips={chips} active={stateFilter} onSelect={setStateFilter} />
        <SearchInput
          className="w-[300px] flex-shrink-0"
          pill={false}
          placeholder="Search contests, IDs, fixtures"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      <div className="flex flex-col gap-3">
        {/* Column widths match contests-list.pcl-table in the design reference. */}
        <Table columns="minmax(190px, 2.1fr) 112px 92px 92px 110px 76px 116px">
          <THead>
            <TCell>Name</TCell>
            <TCell>Status</TCell>
            <TCell>Opens</TCell>
            <TCell>Closes</TCell>
            <TCell>Time in status</TCell>
            <TCell variant="num">Entries</TCell>
            <TCell>Award delivery</TCell>
          </THead>
          {contests.map((contest) => {
            const pill = contestPillState(contest.state, contest.finalisedAt);
            const elapsedMs = timeInStatusMs(contest, new Date());
            const pickLabel = pickCountLabel(contest.config);
            const opens = formatDateParts(contest.opensAt);
            const closes = formatDateParts(contest.closesAt);
            const entriesIdle = pill === 'draft' || pill === 'scheduled';
            return (
              <TRow key={contest.id} href={`/contests/${contest.id}`}>
                <TCell variant="primary">
                  {/* The ⓘ leads the name and carries the full id — the truncated chip beside it
                      stays as the at-a-glance handle. */}
                  <InfoHint entity="Contest" details={contestDetails(contest)}>
                    <span className="min-w-0 truncate text-[14px] font-semibold text-accent">
                      {contest.name}
                    </span>
                    <IdChip className="flex-shrink-0" value={contest.id.slice(0, 8)} />
                  </InfoHint>
                  {pickLabel && <span className="text-[12.5px] text-ink-4">{pickLabel}</span>}
                </TCell>
                <TCell>
                  <StatusPill state={pill} />
                </TCell>
                <TCell variant="stack" primary={opens.date} sub={opens.time} />
                <TCell variant="stack" primary={closes.date} sub={closes.time} />
                <TCell
                  variant="stack"
                  primary={elapsedMs === null ? '—' : formatDuration(elapsedMs)}
                  sub={`in ${pill}`}
                />
                <TCell
                  variant="num"
                  state={contest.state === 'Canceled' ? 'void' : entriesIdle ? 'idle' : undefined}
                >
                  {formatCount(contest.entryCount)}
                </TCell>
                <TCell>
                  {/* Award-delivery percentage (admin-ui.md) has no source: API-23 serves awards a
                      page at a time, and no endpoint aggregates delivery per Contest — computing it
                      here would be one request per row. An em-dash is what this column already
                      showed outside the dev server, where the QA fixtures never rendered. CAE-364
                      carries the aggregate. */}
                  <span className="font-mono text-ink-4">—</span>
                </TCell>
              </TRow>
            );
          })}
        </Table>
        <TableFooter>
          <span>
            Showing{' '}
            <strong>
              {contests.length === 0 ? 0 : pageStart + 1}–{pageStart + contests.length}
            </strong>{' '}
            of <strong>{total}</strong> contests
          </span>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 font-ui text-[13px] text-ink-3">
              Rows per page
              <Select
                aria-label="Rows per page"
                className="w-[76px] min-w-[76px]"
                options={['10', '25', '50']}
                value={pageSize}
                onChange={setPageSize}
              />
            </label>
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                aria-label="Previous page"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="grid h-7 w-7 place-items-center rounded-md border-[0.5px] border-line-strong bg-surface text-ink-3 disabled:cursor-not-allowed disabled:opacity-45"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M15 6l-6 6 6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <span className="font-ui text-[13px] text-ink-3">
                Page <strong className="text-ink">{currentPage}</strong> of{' '}
                <strong className="text-ink">{pageCount}</strong>
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={currentPage >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                className="grid h-7 w-7 place-items-center rounded-md border-[0.5px] border-line-strong bg-surface text-ink-3 disabled:cursor-not-allowed disabled:opacity-45"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M9 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </TableFooter>
      </div>
      {createModalOpen && (
        <CreateContestModal
          client={client}
          configClient={configClient}
          onClose={() => setCreateModalOpen(false)}
          onCreated={(id) => void navigate(`/contests/${id}`)}
        />
      )}
    </AdminShell>
  );
}

interface CreateContestModalProps {
  readonly client: ContestsClient;
  readonly configClient: ConfigClient;
  readonly onClose: () => void;
  readonly onCreated: (id: string) => void;
}

/** The basic-info create step (admin-ui.md, APP-SPEC.md "+ New contest"). Curation, prizes, and
 * rules are the editor's job (CAE-298) — this only lands a Draft contest via the real API-09
 * create route and hands off to the detail page. */
function CreateContestModal({ client, configClient, onClose, onCreated }: CreateContestModalProps) {
  const { getToken } = useAdminAuth();
  const [name, setName] = useState('');
  const [opensAt, setOpensAt] = useState('');
  const [campaignId, setCampaignId] = useState('');
  const [round, setRound] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill the Campaign ID from the configured default (CAE-548, INV-08). The server applies the
  // same default at creation, so this is a convenience: an empty default leaves the field blank and
  // Campaign ID stays optional.
  useEffect(() => {
    const token = getToken();
    if (token === null) return;
    let cancelled = false;
    void (async () => {
      const result = await configClient.getConfig(token);
      if (cancelled || !result.ok) return;
      const configured = result.config['default_campaign_id'];
      if (typeof configured === 'string' && configured !== '') {
        // Don't clobber anything the operator has already typed while this was in flight.
        setCampaignId((current) => (current === '' ? configured : current));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [configClient, getToken]);

  const canSubmit = name.trim() !== '' && opensAt.trim() !== '' && round.trim() !== '';

  async function submit() {
    const token = getToken();
    if (token === null || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    const result = await client.createContest(token, {
      name,
      opensAt: dateTimeLocalToIso(opensAt),
      campaignId,
      round,
    });
    setSubmitting(false);
    if (result.ok) {
      onCreated(result.contest.id);
      return;
    }
    setError(result.message ?? `Could not create this Contest (${result.code ?? result.status}).`);
  }

  return (
    <Modal open onClose={onClose} maxWidth={540}>
      <ModalHead
        context="New contest"
        title="Create a contest"
        subtitle="Set the essentials — you can curate the slate, prizes, and rules next in the editor."
        onClose={onClose}
      />
      <ModalBody>
        <div className="grid grid-cols-2 gap-x-5 gap-y-4 px-7 py-[22px]">
          <Input
            className="col-span-2"
            label="Contest name"
            hint={
              <span className="inline-flex items-center gap-1.5 text-accent">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  />
                  <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.6" />
                </svg>
                Player-visible — served to the game
              </span>
            }
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Input
            label="Opens at"
            type="datetime-local"
            mono
            value={opensAt}
            onChange={(event) => setOpensAt(event.target.value)}
          />
          <Input
            label="Campaign ID"
            mono
            placeholder="CMP-0000"
            value={campaignId}
            onChange={(event) => setCampaignId(event.target.value)}
          />
          <Input
            className="col-span-2"
            label="Round"
            mono
            value={round}
            onChange={(event) => setRound(event.target.value)}
          />
          {error && (
            <div className="col-span-2">
              <Banner variant="danger">{error}</Banner>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFoot
        summary={
          <>
            Creates a <strong className="font-semibold text-ink">Draft</strong> contest
          </>
        }
      >
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={!canSubmit || submitting} onClick={() => void submit()}>
          Create & edit
        </Button>
      </ModalFoot>
    </Modal>
  );
}
